import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CloseIcon } from '@/design-system/icons'

/**
 * "Add to Home Screen" nudge for iOS Safari — iOS doesn't fire the beforeinstallprompt
 * event Android/Chrome uses, so we detect the platform + render our own banner with
 * the exact tap sequence. Dismissible; the dismissal is remembered in localStorage.
 *
 * Rules:
 *  - Only show on iOS Safari (not other iOS browsers, not desktop)
 *  - Only show when NOT already installed (running in browser tab, not standalone)
 *  - Never show if dismissed within the last 30 days
 *  - Delay 3 seconds after mount so it doesn't hit at first-paint
 *
 * On Android + desktop, Chrome/Edge handle install via the native prompt automatically —
 * we still opt into `beforeinstallprompt` capture below so we can show a friendlier
 * "Install BabyOS" button when available.
 */

const DISMISS_KEY = 'babyos.install_prompt.dismissed_at'
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000  // 30 days

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream
  const isSafari = /^((?!chrome|android|fxios|crios|edgios).)*safari/i.test(ua)
  return isIos && isSafari
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return true
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  )
}

function wasRecentlyDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false
  const raw = localStorage.getItem(DISMISS_KEY)
  if (!raw) return false
  const ts = parseInt(raw, 10)
  if (!Number.isFinite(ts)) return false
  return Date.now() - ts < DISMISS_TTL_MS
}

export function InstallPrompt() {
  const [show, setShow] = useState(false)
  const [mode, setMode] = useState<'ios' | 'native' | null>(null)
  const [nativePrompt, setNativePrompt] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return

    // Android/desktop native prompt path
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setNativePrompt(e as BeforeInstallPromptEvent)
      setMode('native')
      const t = setTimeout(() => setShow(true), 3000)
      return () => clearTimeout(t)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    // iOS Safari path
    let iosTimer: number | null = null
    if (isIosSafari()) {
      setMode('ios')
      iosTimer = window.setTimeout(() => setShow(true), 3000)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      if (iosTimer != null) window.clearTimeout(iosTimer)
    }
  }, [])

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch { /* noop */ }
    setShow(false)
  }

  async function acceptNative() {
    if (!nativePrompt) return
    await nativePrompt.prompt()
    const { outcome } = await nativePrompt.userChoice
    if (outcome === 'accepted') setShow(false)
    else dismiss()  // treat as long-term dismiss so we don't nag
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-x-0 z-40 px-3"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 76px)' }}   // above the tab bar
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 220 }}
        >
          <div className="mx-auto max-w-md rounded-r-md border border-divider-strong bg-bg-raised shadow-s3 p-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-r-sm shrink-0 flex items-center justify-center overflow-hidden">
                <img src="/icon-192.png" alt="" className="h-full w-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-body font-semibold text-text-primary">Install Wren</div>
                {mode === 'ios' ? (
                  <div className="mt-1 text-caption text-text-secondary">
                    Tap the <ShareGlyph /> Share button in Safari, then <strong className="text-text-primary">Add to Home Screen</strong>.
                    Runs full-screen, works offline.
                  </div>
                ) : (
                  <div className="mt-1 text-caption text-text-secondary">
                    Install to your home screen for full-screen use + offline access.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                className="shrink-0 -mr-1 -mt-1 h-8 w-8 flex items-center justify-center rounded-full text-text-tertiary hover:bg-white/5"
              >
                <CloseIcon size={16} />
              </button>
            </div>
            {mode === 'native' && (
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={acceptNative} className="pill-btn pill-btn--coral flex-1" style={{ minHeight: 44, padding: '10px 20px' }}>
                  Install
                </button>
                <button type="button" onClick={dismiss} className="pill-btn pill-btn--ghost" style={{ minHeight: 44, padding: '10px 20px' }}>
                  Not now
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function ShareGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="inline align-middle">
      <path d="M12 3v12M8 7l4-4 4 4M6 12v6a2 2 0 002 2h8a2 2 0 002-2v-6" />
    </svg>
  )
}
